import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { CarCard } from "@/components/site/CarCard";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { InnerPageRequirements } from "@/components/site/InnerPageRequirements";
import { ReviewsHighlight } from "@/components/site/ReviewsHighlight";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cars } from "@/lib/cars";
import { Phone } from "lucide-react";
import { breadcrumbScript } from "@/lib/schema";

const weddingFaqs = [
  {
    q: "How long do you hold the car on the wedding day?",
    a: "Every wedding rental is a full 24-hour day at minimum, and most couples book two days: the day before for photos and rehearsal, and the wedding day itself. We arrive with the vehicle 45 minutes before your first scheduled use and collect it the following morning from the reception venue or hotel, so nobody in the party has to drop a car off after the last dance.",
  },
  {
    q: "Can I decorate the car with ribbons, flowers, or a 'Just Married' sign?",
    a: "Yes, with two rules. Anything attached to painted surfaces has to use non-adhesive materials: soft ribbon, magnetic signs, or floral arrangements zip-tied to the grille or door handles are all fine. No suction cups on the paint, no shaving-cream writing on the windows, and no tape of any kind. If you send us photos of your planned setup a week ahead, we'll flag anything that could mark the car.",
  },
  {
    q: "Do you coordinate with our photographer or planner?",
    a: "We do. Send us your photographer's contact and the shot list once the timeline is locked. We'll position the car for portraits with the correct side facing the light, keep it clean between shots with a detailing kit in the trunk, and stay out of frame for candid moments. For getaway shots our driver can pre-stage the car at the exit for the send-off.",
  },
  {
    q: "Which vehicle suits which wedding style?",
    a: "The Mercedes-Benz S-Class is the classic sedan choice for a quiet drive to the ceremony. The Range Rover P530 LWB offers a formal SUV arrival with extra rear-passenger room. The Cadillac Escalade is the wedding-party vehicle: seats seven with room for dresses and suits on hangers. The BMW X7 M60i gives immediate family a modern luxury option. The BMW M440i is a sporty getaway car.",
  },
  {
    q: "Can you deliver to the ceremony venue instead of the hotel?",
    a: "Yes, and most couples prefer that. We commonly split delivery: the getting-ready car (usually the S-Class) goes to the hotel or bridal suite in the morning, and the wedding-party vehicle (Escalade or X7) is staged at the ceremony venue an hour before the guest arrival. Give us both addresses and the driver handoff times at booking.",
  },
  {
    q: "What is the getaway-car process at the end of the night?",
    a: "We stage the getaway vehicle at the reception exit 30 minutes before your scheduled departure, keys ready, engine warm in cool weather. Our driver stays with the car until the send-off, then either hands the keys to the couple for the drive to the hotel, or drives you both directly to your suite if you'd rather not drive after the celebration. Return happens the next morning from the hotel valet. No post-wedding logistics for you.",
  },
  {
    q: "Do you have a backup vehicle in case something happens?",
    a: "Every wedding booking includes a comparable backup vehicle on standby the day of the wedding. If the primary car has any issue (mechanical, cosmetic, or weather-related), a second vehicle from the same tier is already prepped and can be at the venue inside 45 minutes anywhere in Metro Vancouver. This is included at no extra charge on every wedding rental.",
  },
];

const weddingFleet = cars.filter((c) =>
  ["range-rover-p530-lwb-2024", "mercedes-s-class-2022", "cadillac-escalade-2023", "bmw-x7-m60i-2024", "bmw-m440-2021"].includes(c.slug),
);

export const Route = createFileRoute("/wedding-car-rental-vancouver")({
  component: WeddingLanding,
  head: () => ({
    meta: [
      { title: "Wedding Car Rental Vancouver | Avolus Cars" },
       { name: "description", content: "Vancouver wedding car rental with Range Rover P530 LWB, S-Class and Escalade. Timed venue delivery and photographer coordination." },
      { property: "og:title", content: "Wedding Car Rental Vancouver | Avolus" },
      { property: "og:description", content: "Timed venue delivery, decoration guidance, photographer coordination, and a backup vehicle on standby for every Vancouver wedding." },
      { property: "og:url", content: "https://avoluscars.com/wedding-car-rental-vancouver" },
       { property: "og:type", content: "website" },
       { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/wedding-car-rental-vancouver" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Wedding Car Rental",
          provider: { "@id": "https://avoluscars.com/#business" },
          areaServed: [
            { "@type": "City", name: "Vancouver" },
            { "@type": "City", name: "Richmond" },
            { "@type": "City", name: "Burnaby" },
            { "@type": "City", name: "Surrey" },
            { "@type": "City", name: "Whistler" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: weddingFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Wedding Car Rental Vancouver", url: "https://avoluscars.com/wedding-car-rental-vancouver" },
      ]),
    ],
  }),
});

function WeddingLanding() {
  return (
    <Layout inner>
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Vancouver Wedding Car Rental</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground max-w-4xl leading-[1.05]">
              Vancouver Wedding Cars, Timed to the Ceremony
            </h1>
            <nav aria-label="On this page" className="md:hidden mt-4 flex flex-wrap gap-x-5 text-sm underline underline-offset-4"><a href="#vehicles" className="inline-flex items-center min-h-11">Vehicles</a><a href="#questions" className="inline-flex items-center min-h-11">Questions</a><a href="/rates" className="inline-flex items-center min-h-11">Rates</a></nav>
            <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              A wedding rental is not a regular rental. Timing is fixed, the photographer needs
              the car in the right place, and nothing can go wrong on the day. Avolus books
              wedding vehicles as full-service, staged deliveries with a backup car on call,
              across Vancouver, Richmond, Burnaby, Surrey, Coquitlam, Langley, and Whistler.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="/book"
                className="text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 hover:tracking-[0.32em] hover:scale-[1.02] transition-all duration-300"
              >
                Book Now
              </a>
              <a
                href="tel:+17783798881"
                className="md:hidden inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                <Phone size={14} /> 778-379-8881
              </a>
              <a
                href="/contact"
                className="hidden md:inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                Contact Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Wedding-Day Timing</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              How long we hold the car.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Every wedding booking is a minimum 24-hour hold, and most couples book two full
                days: one for the rehearsal or engagement portraits and one for the wedding
                itself. We arrive at the getting-ready location 45 minutes before your first
                scheduled use, stay with the car through the ceremony and reception, and
                collect the vehicle the following morning from the hotel or venue you spent
                the night at. Nobody in the wedding party ever has to drop a car off.
              </p>
              <p>
                If the reception runs long we do not charge overage: wedding rentals are
                priced on the full day, not the hour, and late-night sendoffs are the norm.
                For destination weddings up in Whistler or out to the Fraser Valley, add a
                third day so the return drive isn't rushed.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Photographer Coordination</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              The car works with your shot list.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Send us your photographer's contact and the day-of timeline once it's locked.
                Our driver positions the car for portraits with the correct side facing the
                light, wipes down the paint and glass between shots with a detailing kit kept
                in the trunk, and stays out of frame for candids. For the classic first-look
                shot we can stage the car open-door with the couple approaching; for a
                getaway shot we pre-stage the vehicle at the exit lane before the send-off
                so the departure looks unrehearsed.
              </p>
              <p>
                Popular Vancouver shoot locations we know well: Stanley Park's Prospect
                Point, the Vancouver Art Gallery north plaza, the seawall at Coal Harbour,
                Queen Elizabeth Park quarry gardens, and Cypress Mountain lookout for the
                sunset skyline shot. If you want a specific backdrop, tell us at booking and
                we'll build the driving route into the timeline.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Decoration Policy</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              Ribbons and florals, yes. Tape, no.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Soft ribbon looped through door handles, magnetic "Just Married" signs, and
                floral arrangements zip-tied to the grille or side mirrors are all welcome.
                What we do not allow: any tape on paint or glass, suction-cup mounts,
                shaving-cream writing on windows, or anything adhesive. These are the two
                things that turn a wedding rental into a paint-repair invoice, so we're
                strict about them.
              </p>
              <p>
                If you're bringing a florist to dress the car on-site, send us their
                arrival time and photos of the planned arrangement a week out. We'll confirm
                the setup is safe before the day, and our driver assists with the install
                so nothing scratches the finish.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Vehicle by Wedding Style</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              Which car for which wedding.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                <strong className="text-foreground">Classic and formal: <Link to="/fleet/$slug" params={{ slug: "mercedes-s-class-2022" }} className="underline underline-offset-4 hover:text-foreground">Mercedes-Benz S580 wedding rental</Link>.</strong>{" "}
                The long-wheelbase S-Class has the quietest cabin in the fleet, rear-seat
                recline, and the timeless silhouette that photographs well against any
                backdrop from a church to the Vancouver Club. Best for the bride and immediate
                family from the getting-ready location to the ceremony.
              </p>
              <p>
                 <strong className="text-foreground">Formal SUV arrival: <Link to="/fleet/$slug" params={{ slug: "range-rover-p530-lwb-2024" }} className="underline underline-offset-4 hover:text-foreground">Range Rover P530 LWB wedding rental</Link>.</strong>{" "}
                 The long-wheelbase Range Rover gives rear passengers extra room and makes an elegant entrance at the ceremony or reception. Ask us to confirm the seating layout for your party.
               </p>
               <p>
                <strong className="text-foreground">Wedding party transport: <Link to="/fleet/$slug" params={{ slug: "cadillac-escalade-2023" }} className="underline underline-offset-4 hover:text-foreground">Cadillac Escalade wedding rental</Link>.</strong>{" "}
                Seats seven with second-row captain chairs and enough space for gowns on
                hangers, groomsmen's suits, and the bar cart. The Escalade is the go-to for
                shuttling the party between the ceremony, portrait locations, and the
                reception venue.
              </p>
              <p>
                <strong className="text-foreground">Modern luxury: <Link to="/fleet/$slug" params={{ slug: "bmw-x7-m60i-2024" }} className="underline underline-offset-4 hover:text-foreground">BMW X7 M60i wedding rental</Link>.</strong> Splits
                the difference between the S-Class and the Escalade: three rows, contemporary
                lines, and V8 pace for the drive out to a Fraser Valley or Whistler venue.
                Popular with couples who want the immediate family in one vehicle.
              </p>
              <p>
                <strong className="text-foreground">Getaway car: <Link to="/fleet/$slug" params={{ slug: "bmw-m440-2021" }} className="underline underline-offset-4 hover:text-foreground">BMW M440i getaway rental</Link>.</strong>{" "}
                Drop the top for the send-off and drive through Stanley Park or English Bay
                on the way to the hotel. The M440i is booked as the second vehicle on more
                than half our wedding weekends. Venue outside the city? See our{" "}
                <Link to="/delivery" className="underline underline-offset-4 hover:text-foreground">Metro Vancouver delivery areas</Link>{" "}
                for staging to Whistler, the Fraser Valley, and the Sea-to-Sky corridor.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="vehicles" className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Bridal Fleet</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground max-w-2xl mb-12">
              Cars worthy of the day.
            </h2>
          </Reveal>
          <div className="compact-fleet grid gap-8 md:grid-cols-2">
            {weddingFleet.map((c, i) => (
              <CarCard key={c.slug} car={c} index={i} compact />
            ))}
          </div>
        </div>
      </section>

      <section id="questions" className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Wedding FAQ</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-10">
              Wedding-day questions, answered.
            </h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {weddingFaqs.map((f, i) => (
                <AccordionItem key={i} value={`w-${i}`} className="border-border">
                  <AccordionTrigger className="text-left text-base md:text-lg font-display text-foreground py-6 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal>
            <p className="mt-10 text-sm text-muted-foreground">
              Planning something specific? <Link to="/contact" className="text-foreground underline underline-offset-4">Talk to the concierge</Link>. Every wedding brief is different and we quote each one directly.
            </p>
            <p className="mt-4 text-sm">
              <Link to="/partners" className="text-foreground underline underline-offset-4">
                Wedding planner or photographer? Become an Avolus partner →
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      <ReviewsHighlight eyebrow="Wedding Rental Reviews" />
      <InnerPageRequirements />
    </Layout>
  );
}
